import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Film, Camera, Music, Palette, Zap, Briefcase, Users, Lightbulb, Edit, Scissors, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';

const DiscoverProjectsPage = () => {
  const navigate = useNavigate();
  const [selectedDepartments, setSelectedDepartments] = useState([]);

  const departments = [
    {
      id: 'art-department',
      name: 'Art Department',
      icon: Palette,
      description: 'Set designers, art directors, prop masters',
      color: 'from-orange-500/10 to-red-500/10'
    },
    {
      id: 'camera-department',
      name: 'Camera Department',
      icon: Camera,
      description: 'Cinematographers, camera operators, DPs',
      color: 'from-blue-500/10 to-cyan-500/10'
    },
    {
      id: 'sound-department',
      name: 'Sound Department',
      icon: Music,
      description: 'Sound engineers, boom operators',
      color: 'from-purple-500/10 to-pink-500/10'
    },
    {
      id: 'direction',
      name: 'Direction',
      icon: Film,
      description: 'Directors, creative heads',
      color: 'from-green-500/10 to-emerald-500/10'
    },
    {
      id: 'makeup-hair',
      name: 'Makeup & Hair',
      icon: Palette,
      description: 'Makeup artists, hair stylists',
      color: 'from-pink-500/10 to-rose-500/10'
    },
    {
      id: 'sfx-vfx',
      name: 'SFX / VFX',
      icon: Zap,
      description: 'Visual effects artists, 3D modelers',
      color: 'from-indigo-500/10 to-violet-500/10'
    },
    {
      id: 'production',
      name: 'Production',
      icon: Briefcase,
      description: 'Line producers, production managers',
      color: 'from-yellow-500/10 to-amber-500/10'
    },
    {
      id: 'assistant-directors',
      name: 'Assistant Directors',
      icon: Users,
      description: '1st AD, 2nd AD, floor managers',
      color: 'from-teal-500/10 to-cyan-500/10'
    },
    {
      id: 'lighting-department',
      name: 'Lighting Department',
      icon: Lightbulb,
      description: 'Gaffers, lighting technicians',
      color: 'from-orange-500/10 to-yellow-500/10'
    },
    {
      id: 'post-production',
      name: 'Post Production',
      icon: Edit,
      description: 'Editors, colorists',
      color: 'from-blue-500/10 to-indigo-500/10'
    },
    {
      id: 'script-department',
      name: 'Script Department',
      icon: FileText,
      description: 'Scriptwriters, continuity supervisors',
      color: 'from-purple-500/10 to-blue-500/10'
    },
    {
      id: 'editing',
      name: 'Editing',
      icon: Scissors,
      description: 'Video editors, assistant editors',
      color: 'from-green-500/10 to-teal-500/10'
    }
  ];

  const toggleDepartment = (deptId) => {
    if (selectedDepartments.includes(deptId)) {
      setSelectedDepartments(selectedDepartments.filter(id => id !== deptId));
    } else {
      setSelectedDepartments([...selectedDepartments, deptId]);
    }
  };

  const handleContinue = () => {
    if (selectedDepartments.length === 0) {
      return;
    }
    navigate('/discover/jobs', { state: { departments: selectedDepartments } });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border backdrop-blur-sm bg-background/95 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/')}
                className="hover:bg-accent/10 hover:text-accent"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-3 hover:opacity-80 transition-opacity bg-transparent border-none cursor-pointer"
              >
                <img src="/logo.png" alt="Shoot Base" className="h-14 md:h-20 w-auto object-contain" />
                <h1 className="text-xl md:text-2xl font-bold text-accent" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Shoot Base
                </h1>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-orange-ultralight to-background">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto mb-8"
          >
            <Badge className="mb-4 bg-accent/10 text-accent border-accent/30">
              Discover Projects
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
              Find Your Next
              <span className="text-accent"> Film Project</span>
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              Select the departments you're interested in to see available jobs
            </p>
          </motion.div>
        </div>
      </section>

      {/* Department Selection */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-foreground mb-2">Select Your Departments</h2>
              <p className="text-muted-foreground">Choose one or more departments to see matching job opportunities</p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {departments.map((dept, index) => (
                <motion.div
                  key={dept.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    className={`h-full bg-gradient-to-br ${dept.color} border-2 cursor-pointer transition-all hover:shadow-lg ${
                      selectedDepartments.includes(dept.id) ? 'border-accent' : 'border-border hover:border-accent/50'
                    }`}
                    onClick={() => toggleDepartment(dept.id)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          selectedDepartments.includes(dept.id) ? 'bg-accent' : 'bg-accent/10'
                        } transition-colors`}>
                          <dept.icon className={`w-6 h-6 ${
                            selectedDepartments.includes(dept.id) ? 'text-white' : 'text-accent'
                          }`} />
                        </div>
                        <Checkbox
                          checked={selectedDepartments.includes(dept.id)}
                          onCheckedChange={() => toggleDepartment(dept.id)}
                          className="mt-1"
                        />
                      </div>
                      <h3 className="text-lg font-semibold mb-2 text-foreground">
                        {dept.name}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {dept.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Selected Departments Summary */}
            {selectedDepartments.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-8 p-6 bg-accent/5 rounded-lg border border-accent/20"
              >
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h3 className="font-semibold text-foreground mb-2">Selected Departments: {selectedDepartments.length}</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedDepartments.map(id => {
                        const dept = departments.find(d => d.id === id);
                        return (
                          <Badge key={id} className="bg-accent text-white">
                            {dept?.name}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                  <Button
                    onClick={handleContinue}
                    className="bg-accent hover:bg-accent/90 text-white font-semibold group"
                    size="lg"
                  >
                    View Jobs
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </motion.div>
            )}

            {selectedDepartments.length === 0 && (
              <div className="mt-8 text-center p-8 bg-muted/30 rounded-lg border border-border">
                <p className="text-muted-foreground">Select at least one department to continue</p>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DiscoverProjectsPage;